
import React, { useEffect, useState } from 'react';
import axios, { decryptData } from '../../Axiosinstance';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Loader from '../../Loader';
import { baseURLdyte, Password, Username } from '../../common';
import 'bootstrap/dist/css/bootstrap.min.css';

const MeetingAttendanceView = () => {
    const { batchId } = useParams();
    const batchId1 = decryptData(decodeURIComponent(batchId));

    const [sessions, setSessions] = useState([]);
    const [batchDetails, setBatchDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [batchLoading, setBatchLoading] = useState(true);

    // Date range filter
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    // Session list pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);

    // ── 1. Fetch batch details ─────────────────────────────────────────────────
    useEffect(() => {
        const fetchBatch = async () => {
            try {
                const res = await axios.get(`batch/${batchId1}`);
                setBatchDetails(res.data);
            } catch (err) {
                console.error('Error fetching batch:', err);
            } finally {
                setBatchLoading(false);
            }
        };
        fetchBatch();
    }, [batchId1]);

    // ── 2. Fetch Dyte sessions once batch details are ready ────────────────────
    useEffect(() => {
        if (!batchDetails) return;

        const fetchSessions = async () => {
            setLoading(true);
            setSessions([]);
            setPage(0);

            try {
                const authHeader = `Basic ${btoa(`${Username}:${Password}`)}`;
                const res = await axios.get(`${baseURLdyte}/sessions`, {
                    headers: { Authorization: authHeader },
                });

                const allSessions = res.data.data.sessions;
                const url = batchDetails.onlineclasslink;
                const match = url?.match(/id=([a-f\d-]+)/i);

                if (!match || !match[1]) {
                    console.warn('Meeting ID not found in onlineclasslink');
                    setLoading(false);
                    return;
                }

                const meetingId = match[1];

                const filtered = allSessions.filter(session => {
                    const isSameMeeting =
                        session.meta?.room_name === meetingId ||
                        session.associated_id === meetingId;
                    if (!isSameMeeting) return false;
                    if (session.total_participants === 0) return false;
                    if (!session.ended_at) return false;
                    return true;
                });

                // Most recently conducted session appears first
                filtered.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
                setSessions(filtered);
            } catch (err) {
                console.error('Error fetching sessions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSessions();
    }, [batchDetails]);

    // ── 3. Reset page when filter changes ─────────────────────────────────────
    useEffect(() => {
        setPage(0);
    }, [filterStartDate, filterEndDate]);

    // ── 4. Apply date range filter ────────────────────────────────────────────
    const filteredSessions = sessions.filter(session => {
        const sessionDate = new Date(session.started_at);
        if (filterStartDate) {
            const start = new Date(filterStartDate);
            start.setHours(0, 0, 0, 0);
            if (sessionDate < start) return false;
        }
        if (filterEndDate) {
            const end = new Date(filterEndDate);
            end.setHours(23, 59, 59, 999);
            if (sessionDate > end) return false;
        }
        return true;
    });

    const handleClearFilter = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setPage(0);
    };

    // ── 5. View attendance for a session ──────────────────────────────────────
    // const handleViewAttendance = async (session) => {
    //     setSelectedSession(session);
    //     setShowModal(true);
    //     setAttendanceLoading(true);
    //     try {
    //         const res = await axios.get(
    //             `/online-meeting/meeting-attendance/${batchId1}`
    //         );
    //         const sessionRecords = res.data.filter(r => r.sessionId === session.id);
    //         setAttendanceRecords(sessionRecords);
    //     } catch (err) {
    //         console.error('Error fetching attendance:', err);
    //         setAttendanceRecords([]);
    //     } finally {
    //         setAttendanceLoading(false);
    //     }
    // };

    const handleViewAttendance = async (session) => {
    setSelectedSession(session);
    setShowModal(true);
    setAttendanceLoading(true);
    try {
        const res = await axios.get(
            `/online-meeting/meeting-attendance/${batchId1}/session/${session.id}` // ✅ sessionId passed
        );
        setAttendanceRecords(res.data); // ✅ no client-side filter needed
    } catch (err) {
        console.error('Error fetching attendance:', err);
        setAttendanceRecords([]);
    } finally {
        setAttendanceLoading(false);
    }
};

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedSession(null);
        setAttendanceRecords([]);
    };

    // ── Helpers ────────────────────────────────────────────────────────────────
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    // const formatDuration = (seconds) => {
    //     if (!seconds && seconds !== 0) return 'N/A';
    //     const mins = Math.floor(seconds / 60);
    //     const secs = Math.round(seconds % 60);
    //     return `${mins}m ${secs}s`;
    // };

    const formatDuration = (minutesConsumed) => {
    if (minutesConsumed == null) return 'N/A';

    const totalSeconds = Math.round(minutesConsumed * 60);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
    }

    return `${minutes}m ${seconds}s`;
};

    const formatMinutes = (minutes) => {
        if (minutes == null) return '—';
        return `${parseFloat(minutes).toFixed(2)} mins`;
    };

    const presentCount = attendanceRecords.filter(r => r.status === 'Present').length;
    const absentCount = attendanceRecords.filter(r => r.status === 'Absent').length;

    const paginatedSessions = filteredSessions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (batchLoading) return <Loader />;

    return (
        <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>

            {/* ── Page Header ────────────────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg, #1565C0, #1976D2)',
                color: 'white',
                padding: '20px 24px',
                borderRadius: '10px',
                marginBottom: '24px',
            }}>
                <h4 style={{ margin: 0, fontWeight: 'bold' }}>Online Sessions</h4>
                <div style={{ display: 'flex', gap: '24px', marginTop: '10px', flexWrap: 'wrap', fontSize: '14px' }}>
                    <span><strong>Batch:</strong> {batchId1}</span>
                    <span><strong>Course:</strong> {batchDetails?.course || 'N/A'}</span>
                    <span><strong>Total Sessions:</strong> {filteredSessions.length}</span>
                </div>
            </div>

            {/* ── Date Range Filter ─────────────────────────────────────────────── */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: '10px' }}>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#1565C0', marginBottom: '12px' }}>
                    Filter by Date Range
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '12px', color: '#555', fontWeight: '500' }}>Start Date</label>
                        <input
                            type="date"
                            value={filterStartDate}
                            onChange={e => setFilterStartDate(e.target.value)}
                            max={filterEndDate || undefined}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1.5px solid #ccc',
                                fontSize: '14px',
                                outline: 'none',
                                cursor: 'pointer',
                                color: filterStartDate ? '#1565C0' : '#aaa',
                            }}
                        />
                    </div>
                    <div style={{ paddingTop: '18px', color: '#888', fontWeight: '600' }}>→</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '12px', color: '#555', fontWeight: '500' }}>End Date</label>
                        <input
                            type="date"
                            value={filterEndDate}
                            onChange={e => setFilterEndDate(e.target.value)}
                            min={filterStartDate || undefined}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1.5px solid #ccc',
                                fontSize: '14px',
                                outline: 'none',
                                cursor: 'pointer',
                                color: filterEndDate ? '#1565C0' : '#aaa',
                            }}
                        />
                    </div>
                    {(filterStartDate || filterEndDate) && (
                        <button
                            onClick={handleClearFilter}
                            style={{
                                marginTop: '18px',
                                padding: '8px 18px',
                                borderRadius: '8px',
                                border: '1.5px solid #dc3545',
                                backgroundColor: 'white',
                                color: '#dc3545',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                            }}
                        >
                            ✕ Clear Filter
                        </button>
                    )}
                    {(filterStartDate || filterEndDate) && (
                        <div style={{
                            marginTop: '18px',
                            padding: '8px 14px',
                            borderRadius: '8px',
                            backgroundColor: '#e3f2fd',
                            color: '#1565C0',
                            fontSize: '13px',
                        }}>
                            Showing <strong>{filteredSessions.length}</strong> of <strong>{sessions.length}</strong> sessions
                        </div>
                    )}
                </div>
            </Paper>

            {/* ── Sessions Table ──────────────────────────────────────────────────── */}
            {loading ? (
                <Loader />
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '10px' }}>
                    <TableContainer>
                        <Table aria-label="sessions table">
                            <TableHead>
                                <TableRow style={{ backgroundColor: '#0D47A1' }}>
                                    {['S.No', 'Session Date', 'Meeting Title', 'Duration',
                                        'Ended At', 'Status', 'Attendance'].map(col => (
                                            <TableCell
                                                key={col}
                                                sx={{
                                                    color: '#fff !important',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                    letterSpacing: '0.5px',
                                                    backgroundColor: '#0D47A1 !important',
                                                }}
                                            >
                                                {col}
                                            </TableCell>
                                        ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedSessions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                                            <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                                                {(filterStartDate || filterEndDate)
                                                    ? 'No sessions found for the selected date range.'
                                                    : 'No sessions found for this batch.'}
                                            </div>
                                            {(filterStartDate || filterEndDate) && (
                                                <div style={{ fontSize: '13px' }}>
                                                    Try adjusting or clearing the date filter.
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedSessions.map((session, index) => (
                                        <TableRow
                                            key={session.id}
                                            hover
                                            style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}
                                        >
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(session.started_at)}
                                            </TableCell>
                                            <TableCell>{session.meeting_display_name || 'N/A'}</TableCell>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>
                                                {formatDuration(session.minutes_consumed)}
                                            </TableCell>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(session.ended_at)}
                                            </TableCell>
                                            <TableCell>
                                                <span style={{
                                                    backgroundColor: '#d4edda', color: '#155724',
                                                    padding: '3px 10px', borderRadius: '12px',
                                                    fontSize: '12px', fontWeight: 'bold'
                                                }}>
                                                    Ended
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleViewAttendance(session)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredSessions.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </Paper>
            )}

            {/* ── Attendance Modal ───────────────────────────────────────────────── */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton style={{ backgroundColor: '#1565C0', color: 'white' }}>
                    <Modal.Title style={{ fontSize: '17px' }}>
                        Attendance — {selectedSession?.meeting_display_name || 'Session'}
                        <span style={{ fontSize: '13px', fontWeight: 'normal', marginLeft: '10px', opacity: 0.85 }}>
                            {selectedSession ? formatDate(selectedSession.started_at) : ''}
                        </span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {attendanceLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <div className="spinner-border text-primary" role="status" />
                            <p style={{ marginTop: '12px', color: '#555' }}>Loading attendance records...</p>
                        </div>
                    ) : attendanceRecords.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                                No attendance records found for this session.
                            </div>
                            <div style={{ fontSize: '13px' }}>
                                Attendance is automatically saved when a meeting ends via webhook.
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* ── Summary Cards ─────────────────────────────────────────── */}
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                                {[
                                    { label: 'Present', count: presentCount, bg: '#e8f5e9', color: '#2e7d32' },
                                    { label: 'Absent', count: absentCount, bg: '#fce4ec', color: '#c62828' },
                                    { label: 'Total', count: attendanceRecords.length, bg: '#e3f2fd', color: '#1565C0' },
                                    {
                                        label: 'Attendance %',
                                        count: attendanceRecords.length > 0
                                            ? `${Math.round((presentCount / attendanceRecords.length) * 100)}%`
                                            : '0%',
                                        bg: '#fff8e1', color: '#f57f17'
                                    },
                                ].map(card => (
                                    <div key={card.label} style={{
                                        flex: 1, minWidth: '100px', padding: '12px',
                                        borderRadius: '10px', backgroundColor: card.bg, textAlign: 'center',
                                    }}>
                                        <div style={{ fontSize: '26px', fontWeight: 'bold', color: card.color }}>
                                            {card.count}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>
                                            {card.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── Attendance Table ──────────────────────────────────────── */}
                            <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
                                <Table size="small">
                                    <TableHead style={{ backgroundColor: '#f0f4ff' }}>
                                        <TableRow>
                                            {['S.No', 'Student Name', 'Status', 'Joined At', 'Left At', 'Duration'].map(col => (
                                                <TableCell key={col} style={{ fontWeight: 'bold', color: '#333' }}>
                                                    {col}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {attendanceRecords.map((record, index) => (
                                            <TableRow
                                                key={record.id || index}
                                                hover
                                                style={{
                                                    backgroundColor: record.status === 'Absent'
                                                        ? '#fff5f5'
                                                        : index % 2 === 0 ? '#fafafa' : 'white'
                                                }}
                                            >
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell style={{ fontWeight: '500' }}>{record.studentName}</TableCell>
                                                <TableCell>
                                                    {record.status === 'Present' ? (
                                                        <span style={{
                                                            backgroundColor: '#d4edda', color: '#155724',
                                                            padding: '3px 10px', borderRadius: '12px',
                                                            fontSize: '12px', fontWeight: 'bold'
                                                        }}>✅ Present</span>
                                                    ) : (
                                                        <span style={{
                                                            backgroundColor: '#f8d7da', color: '#721c24',
                                                            padding: '3px 10px', borderRadius: '12px',
                                                            fontSize: '12px', fontWeight: 'bold'
                                                        }}>❌ Absent</span>
                                                    )}
                                                </TableCell>
                                                <TableCell style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                                                    {record.joinedAt ? formatDate(record.joinedAt) : '—'}
                                                </TableCell>
                                                <TableCell style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                                                    {record.leftAt ? formatDate(record.leftAt) : '—'}
                                                </TableCell>
                                                <TableCell>{formatMinutes(record.durationMinutes)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button
                        onClick={handleCloseModal}
                        style={{
                            background: '#dc3545', color: 'white', border: 'none',
                            padding: '8px 24px', borderRadius: '6px', fontSize: '15px', cursor: 'pointer'
                        }}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default MeetingAttendanceView;